import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import DashboardHeader from "./DashboardHeader";
import DashboardSidebar from "./DashboardSidebar";

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  transition:
    background 0.3s ease,
    color 0.3s ease;
`;

const MainContent = styled.main`
  flex: 1;
  /* A row-flex item's automatic minimum size is its content's min-content
     width, so a wide data grid (Invoice Reconcile) would push this whole
     column past the viewport — and html's overflow-x: clip makes that
     unreachable. min-width: 0 lets the column shrink; wide content scrolls
     inside its own container instead. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  transition: ${({ theme }) => theme.transitions.normal};
  margin-left: ${({ theme, $sidebarCollapsed }) =>
    $sidebarCollapsed
      ? theme.layout.sidebarCollapsedWidth
      : theme.layout.sidebarWidth};

  ${({ theme }) => theme.mediaQuery.maxMd} {
    margin-left: 0;
  }
`;

const HeaderWrapper = styled.div`
  position: fixed;
  top: 0;
  left: ${({ theme, $sidebarCollapsed }) =>
    $sidebarCollapsed
      ? theme.layout.sidebarCollapsedWidth
      : theme.layout.sidebarWidth};
  right: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};

  ${({ theme }) => theme.mediaQuery.maxMd} {
    left: 0;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: ${({ theme, $fullBleed }) =>
    $fullBleed ? "0" : theme.layout.contentPadding};
  padding-top: ${({ theme, $fullBleed }) =>
    $fullBleed
      ? theme.layout.headerHeight
      : `calc(${theme.layout.headerHeight} + ${theme.layout.contentPadding})`};
  overflow-y: auto;
  max-width: 100%;
`;

// App takeover: no ODIN sidebar/header at all — the agent shell owns the
// whole viewport (used by WorkspaceShell's maximised mode). No header offset
// to compensate for, unlike fullBleed's ContentArea above.
const TakeoverContent = styled.div`
  flex: 1;
  min-width: 0; /* same automatic-minimum-size trap as MainContent */
  height: 100vh;
  overflow: hidden;
  max-width: 100%;
`;

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndex.overlay};
  display: ${({ $show }) => ($show ? "block" : "none")};

  ${({ theme }) => theme.mediaQuery.lg} {
    display: none;
  }
`;

const DashboardLayout = ({
  children,
  className,
  fullBleed = false,
  takeover = false,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (takeover) {
    return (
      <LayoutContainer className={className}>
        <TakeoverContent>{children}</TakeoverContent>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer className={className}>
      <DashboardSidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileMenuOpen}
        onToggle={toggleSidebar}
        onMobileClose={closeMobileMenu}
      />

      <MainContent $sidebarCollapsed={sidebarCollapsed}>
        <HeaderWrapper $sidebarCollapsed={sidebarCollapsed}>
          <DashboardHeader
            onToggleSidebar={toggleSidebar}
            onToggleMobileMenu={toggleMobileMenu}
            sidebarCollapsed={sidebarCollapsed}
          />
        </HeaderWrapper>

        <ContentArea $fullBleed={fullBleed}>{children}</ContentArea>
      </MainContent>

      <MobileOverlay $show={mobileMenuOpen} onClick={closeMobileMenu} />
    </LayoutContainer>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  fullBleed: PropTypes.bool,
  takeover: PropTypes.bool,
};

export default DashboardLayout;
